import { useEffect, useState } from 'react'
import { PublicKey } from '@solana/web3.js'
import { TOKEN_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID } from '@solana/spl-token'
import BN from 'bn.js'
import { newConnection } from '../lib/tx'
import { MINTS } from '../api/trade'

const NATIVE_SOL_DECIMALS = 9

/**
 * Derive the canonical Associated Token Account address for a legacy Token-program mint.
 * Equivalent to @solana/spl-token getAssociatedTokenAddressSync(mint, owner).
 * Exported so CollateralForm can use the same derivation when building consolidation ixs.
 */
export function findCanonicalAta(owner: PublicKey, mint: PublicKey): PublicKey {
  const [ata] = PublicKey.findProgramAddressSync(
    [owner.toBuffer(), TOKEN_PROGRAM_ID.toBuffer(), mint.toBuffer()],
    ASSOCIATED_TOKEN_PROGRAM_ID,
  )
  return ata
}

export interface SplBalance {
  /** Canonical ATA balance — what the Jupiter Lend SDK uses as tx source. */
  raw: BN
  decimal: string
  decimals: number
  loading: boolean
  accountMissing: boolean
  /**
   * Total balance across ALL token accounts (canonical + non-canonical).
   * Used for display and MAX button so the user sees what they actually hold.
   * May exceed `raw` when tokens are in a non-canonical account.
   */
  totalRaw: BN
  totalDecimal: string
  /**
   * true when the user has jupSOL in non-canonical accounts and the canonical
   * ATA has less than totalRaw. CollateralForm will prepend a consolidation
   * transfer instruction before calling getOperateIx.
   */
  needsConsolidation: boolean
}

/**
 * RPC-direct wallet balance for a single mint. Authoritative for building
 * transactions that transfer OUT of the user's wallet.
 *
 * Reads the canonical ATA directly (same derivation as the Jupiter Lend SDK) so
 * `raw` always matches the account the SDK will use as the tx source. If the
 * user's tokens are in a non-canonical account, `needsConsolidation` is set
 * and `totalRaw` shows the full available balance across all accounts.
 */
export function useSplBalance(
  owner: PublicKey | null | undefined,
  mint: string | null | undefined,
  intervalMs = 15_000,
): SplBalance {
  const defaultDecimals = mint === MINTS.SOL || mint === MINTS.jupSOL ? 9 : mint === MINTS.USDC ? 6 : 9
  const [state, setState] = useState<SplBalance>({
    raw: new BN(0),
    decimal: '0',
    decimals: defaultDecimals,
    loading: true,
    accountMissing: false,
    totalRaw: new BN(0),
    totalDecimal: '0',
    needsConsolidation: false,
  })

  useEffect(() => {
    if (!owner || !mint) {
      setState(s => ({ ...s, loading: false }))
      return
    }
    let cancelled = false
    const conn = newConnection()

    const fetchOnce = async () => {
      try {
        if (mint === MINTS.SOL) {
          const lamports = await conn.getBalance(owner, 'confirmed')
          if (cancelled) return
          const bn = new BN(lamports.toString())
          const dec = (lamports / 1e9).toString()
          setState({
            raw: bn, decimal: dec,
            decimals: NATIVE_SOL_DECIMALS,
            loading: false, accountMissing: false,
            totalRaw: bn, totalDecimal: dec, needsConsolidation: false,
          })
          return
        }

        const mintPk = new PublicKey(mint)
        const ata = findCanonicalAta(owner, mintPk)

        // Step 1: read canonical ATA balance.
        let canonicalRaw = new BN(0)
        let canonicalDecimal = '0'
        let decimals = defaultDecimals
        let ataExists = false

        try {
          const info = await conn.getTokenAccountBalance(ata, 'confirmed')
          canonicalRaw    = new BN(info.value.amount)
          canonicalDecimal = info.value.uiAmountString ?? '0'
          decimals        = info.value.decimals
          ataExists       = true
        } catch {
          // Canonical ATA missing — check non-canonical accounts below.
        }
        if (cancelled) return

        // Step 2: if canonical ATA is empty, look for balance in non-canonical accounts.
        let totalRaw    = canonicalRaw
        let totalDecimal = canonicalDecimal
        let needsConsolidation = false

        if (canonicalRaw.isZero()) {
          try {
            const res = await conn.getParsedTokenAccountsByOwner(owner, { mint: mintPk }, 'confirmed')
            if (cancelled) return
            let sum = new BN(0)
            for (const acct of res.value) {
              const data = acct.account.data
              if (!('parsed' in data)) continue
              const amt = new BN(data.parsed?.info?.tokenAmount?.amount ?? '0')
              sum = sum.add(amt)
              if (!acct.pubkey.equals(ata) && !amt.isZero()) {
                needsConsolidation = true
                decimals = data.parsed?.info?.tokenAmount?.decimals ?? decimals
              }
            }
            totalRaw     = sum
            totalDecimal = sum.isZero()
              ? '0'
              : (Number(sum.toString()) / Math.pow(10, decimals)).toFixed(decimals).replace(/\.?0+$/, '')
          } catch {
            // Ignore — totalRaw stays at canonicalRaw (0)
          }
        }

        if (cancelled) return
        setState({
          raw: canonicalRaw,
          decimal: canonicalDecimal,
          decimals,
          loading: false,
          accountMissing: !ataExists && totalRaw.isZero(),
          totalRaw,
          totalDecimal,
          needsConsolidation,
        })
      } catch {
        if (cancelled) return
        setState(s => ({ ...s, loading: false }))
      }
    }

    fetchOnce()
    const id = setInterval(fetchOnce, intervalMs)
    return () => { cancelled = true; clearInterval(id) }
  }, [owner?.toBase58(), mint, intervalMs])

  return state
}
